// NOTE: NSAI-5657 / NSAI-5658 — saved dashboards. API routes, React Query keys
// and the limits both clients enforce before the server does.
import type { IDashboardSubUrlProps } from "../types/dashboards";

export const DASHBOARDS_API_PATH = "/dashboards";

export const dashboardUrl = (dashboardUuid: string): string =>
  `${DASHBOARDS_API_PATH}/${encodeURIComponent(dashboardUuid)}`;

export const dashboardSubUrl = ({
  dashboardUuid,
  segment,
}: IDashboardSubUrlProps): string =>
  `${dashboardUrl(dashboardUuid)}/${segment}`;

export const DASHBOARD_URL_SEGMENTS = {
  ARCHIVE: "archive",
  UNARCHIVE: "unarchive",
  DUPLICATE: "duplicate",
  PIN: "pin",
  TEAM_PIN: "team-pin",
  SCHEDULE: "schedule",
  CONTENT: "content",
  REFRESH: "refresh",
  VERSIONS: "versions",
  SHARES: "shares",
  TEAM_ACCESS: "team-access",
  SHARE_CANDIDATES: "share-candidates",
} as const;

// NOTE: React Query keys. Every dashboard key starts with DASHBOARDS_QUERY_KEY
// so one invalidation after a mutation refreshes the list, the switcher and the
// open dashboard together.
export const DASHBOARDS_QUERY_KEY = "dashboards";
export const DASHBOARD_DETAILS_QUERY_KEY = "details";
export const DASHBOARD_CONTENT_QUERY_KEY = "content";
export const DASHBOARD_REFRESH_QUERY_KEY = "refresh";
export const DASHBOARD_VERSIONS_QUERY_KEY = "versions";
export const DASHBOARD_VERSION_CONTENT_QUERY_KEY = "version-content";
export const DASHBOARD_SHARES_QUERY_KEY = "shares";
export const DASHBOARD_SHARE_CANDIDATES_QUERY_KEY = "share-candidates";

// NOTE: How often an open dashboard asks whether its refresh has finished. A
// refresh is an agent run — seconds to a couple of minutes — so a socket
// channel would buy little over a light poll of a ~300-byte summary.
export const DASHBOARD_REFRESH_POLL_INTERVAL_MS = 3000;

// NOTE: NSAI-5657 — the server answers 404 for a dashboard that is gone and
// for one the caller may no longer open (it never says which), and 403 once
// the role lost dashboard:read.
export const DASHBOARD_NOT_FOUND_STATUSES: ReadonlySet<number> = new Set([
  403, 404,
]);

// NOTE: Data older than this is re-pulled when a live dashboard is opened
// (refresh-on-open). Mirrors DASHBOARD_REFRESH_ON_OPEN_STALE_MS on the server,
// which is the one that decides `shouldRefreshOnOpen`; the client copy only
// labels the data as stale.
export const DASHBOARD_REFRESH_STALE_AFTER_MS = 15 * 60 * 1000;

export const DASHBOARD_NAME_MAX_LENGTH = 120;
export const DASHBOARD_DATA_REQUEST_MAX_LENGTH = 4000;

// NOTE: Monthly schedules stop at the 28th so every month has the day.
export const DASHBOARD_SCHEDULE_MAX_DAY_OF_MONTH = 28;
export const DASHBOARD_DEFAULT_SCHEDULE_HOUR = 8;
export const DASHBOARD_DEFAULT_SCHEDULE_MINUTE = 0;
// NOTE: Monday.
export const DASHBOARD_DEFAULT_SCHEDULE_DAY_OF_WEEK = 1;
export const DASHBOARD_DEFAULT_SCHEDULE_DAY_OF_MONTH = 1;

// NOTE: Scale the list-card preview renders the page at: the page lays out at
// this width and is shrunk to the card, so a dashboard reads as itself rather
// than as a phone layout squeezed into a tile.
export const DASHBOARD_PREVIEW_LAYOUT_WIDTH_PX = 1280;
export const DASHBOARD_PREVIEW_LAYOUT_HEIGHT_PX = 800;

// NOTE: Web routes; mobile has its own PATHS table with the same shape.
export const DASHBOARDS_ROUTE_PATH = "/dashboards";
export const buildDashboardRoutePath = (dashboardUuid: string): string =>
  `${DASHBOARDS_ROUTE_PATH}/${encodeURIComponent(dashboardUuid)}`;

// NOTE: i18n keys under `dashboards:weekdays`, indexed by the schedule's
// dayOfWeek (0 = Sunday, the cron convention the server uses).
export const DASHBOARD_WEEKDAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;
